import { LOAD_MESSAGES_COUNT } from 'store/constant';
import { store } from 'store';
import { ChatUserModel, ChatUserModelOriginal } from 'types/chat-users/chat-user';
import { ChatUsersFiltersRequest } from 'types/chat-users/filters';
import { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { ChatUserBotStatusType, ChatUserConversation, SendMessageParamsType } from 'types/chat/props';
import { fetcher } from 'utils/axios';
import { getCurrentDateFormatted } from 'utils/helpers';
import { ContactResponse } from 'types/contacts/contact-response';
import { mapContactToChatUser } from 'utils/contact-mapper';
import { convertCustomFiltersToApiFilters } from 'utils/convert-filters-to-api';
import { convertGetMessagesToHistoryMessage } from 'utils/convert-messages';

//* history messages
export async function getChatMessages(pagination: number, phoneNumber: string, count: number | null = null) {
  let result: {
    isActive: boolean | null;
    messages: HistoryMessage[];
    result: boolean;
  } = {
    isActive: null,
    messages: [],
    result: false,
  };
  try {
    const data = await fetcher(
      'get',
      `/api/services/getMessages/${phoneNumber}/${pagination}/${count || LOAD_MESSAGES_COUNT}`,
      'newapi',
    );

    if (data && Array.isArray(data)) {
      const messagesList = data.map(convertGetMessagesToHistoryMessage);
      result = {
        isActive: null,
        messages: messagesList,
        result: true,
      };
    }
  } catch (error) {
    console.warn('getChatMessages error: ', error);
  }

  return result;
}

export async function getChatMessagesFromId(
  id: number,
  phoneNumber: string,
  count: number | null = null,
  type: 'older' | 'newer',
  currentPage: number = 1,
) {
  let result: {
    isActive: boolean | null;
    messages: HistoryMessage[];
    result: boolean;
  } = {
    isActive: null,
    messages: [],
    result: false,
  };

  try {
    let nextPage = currentPage;
    if (type === 'older') {
      nextPage = currentPage + 1; // טעינת עמוד קודם (מספר עמוד גבוה יותר)
    } else if (type === 'newer') {
      nextPage = Math.max(1, currentPage - 1); // טעינת עמוד חדש יותר (מספר עמוד נמוך יותר)
    }

    const data = await fetcher(
      'get',
      `/api/services/getMessages/${phoneNumber}/${nextPage}/${count || LOAD_MESSAGES_COUNT}`,
      'newapi',
    );

    if (data && Array.isArray(data)) {
      const messagesList = data.map(convertGetMessagesToHistoryMessage);
      result = {
        isActive: null,
        messages: messagesList,
        result: true,
      };
    }
  } catch (error) {
    console.log('getChatMessagesFromId error: ', error);
  }

  return result;
}

export async function getChatSearchMessagesArr(phoneNumber: string) {
  let result: HistoryMessage[] = [];
  try {
    const data = await fetcher(
      'get',
      ['/api/whatsapp/searchChatUserWhatsappMessage', { data: { phoneNumberId: phoneNumber } }],
      'history',
    );

    if (data) {
      result = [...data].map((msg) => ({
        ...msg,
        // המרת "outgoing" ל-"outcoming" כדי להתאים למבנה הפנימי
        // השרת שולח "outgoing" אבל הקוד מצפה ל-"outcoming"
        Type: (msg.Type as string) === 'outgoing' ? 'outcoming' : msg.Type,
      }));
    }
  } catch (error) {
    console.log('searchChatUserWhatsappMessage error: ', error);
  }

  return result;
}

export const getChatMissingHistory = async (
  phoneNumber: string,
  currentMsgs: Array<HistoryMessage | MessageModel>,
) => {
  try {
    let dateFrom,
      dateTo,
      closestBufferIndex = -1;
    if (currentMsgs?.length) {
      closestBufferIndex = currentMsgs.findLastIndex((msg) => msg.FileType === 'buffer');
      const lastMessage = currentMsgs[currentMsgs.length - 1];

      dateTo = lastMessage.DateCreatedOriginal;
      dateFrom = currentMsgs[closestBufferIndex !== -1 ? closestBufferIndex + 1 : 0].DateCreatedOriginal;
    } else {
      const date = getCurrentDateFormatted();
      dateFrom = date;
      dateTo = date;
    }

    const filteredMessages =
      closestBufferIndex !== -1 ? currentMsgs.slice(closestBufferIndex + 1) : currentMsgs;
    const data = await fetcher(
      'post',
      [
        '/api/sendMessage/getMissingMessages',
        {
          data: {
            date: dateFrom,
            dateTo: dateTo,
            ids: filteredMessages.map((msg) => msg.Id),
            chatUserPhoneNumberId: phoneNumber.startsWith('972') ? '0' + phoneNumber.slice(3) : phoneNumber,
          },
        },
      ],
      'history',
    );

    if (data?.messages) {
      return (data.messages as Array<HistoryMessage | MessageModel>).map((msg) => ({
        ...msg,
        // המרת "outgoing" ל-"outcoming" כדי להתאים למבנה הפנימי
        // השרת שולח "outgoing" אבל הקוד מצפה ל-"outcoming"
        Type: (msg.Type as string) === 'outgoing' ? 'outcoming' : msg.Type,
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.log('getChatUpdateHistory err: ', error);
    return [];
  }
};

export const fetchGetFavouriteMessages = async (page: number, number: number) => {
  try {
    const data = await fetcher(
      'get',
      ['/api/massMessaging/getFavouriteMessages', { data: { page, number } }],
      'history',
    );
    if (data) {
      return data as {
        result: boolean;
        faivourites: Array<{
          message: HistoryMessage | MessageModel;
          chatUser?: ChatUserModelOriginal | null;
          file?: string | null;
        }>;
      };
    } else return null;
  } catch (error) {
    console.warn('fetchGetFavouriteMessages error: ', error);
    return null;
  }
};

export const fetchRemoveFromFavourites = async (id: number) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/updateMessageFavouriteStatus',
        {
          data: {
            id,
            isFavourite: false,
          },
        },
      ],
      'history',
    );
    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('fetchRemoveFromFavourites error: ', error);
    return null;
  }
};

//* Send message
export async function sendChatMessage(params: SendMessageParamsType) {
  let result: boolean = false;
  try {
    const data = await fetcher(
      'post',
      ['/api/whatsapp/SendOutgoingMessage', { data: params }],
      'messages',
    );

    if (data) {
      result = data.result ?? false;
    }
  } catch (error) {
    console.log('sendChatTextMessage error: ', error);
  }

  return result;
}

export async function sendChatFileMessage(formData: FormData) {
  let result: boolean = false;
  try {
    const data = await fetcher(
      'post',
      ['/api/whatsapp/SendOutgoingMessageWithFile', { data: formData }],
      'messages',
    );

    if (data) {
      return data;
    }
  } catch (error) {
    console.log('sendChatFileMessage error: ', error);
  }

  return result;
}

//* Messages common
export async function fetchSetMessageFavourite(id: number, state: boolean) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/updateMessageFavouriteStatus',
        {
          data: {
            id,
            isFavourite: state,
          },
        },
      ],
      'history',
    );

    return data;
  } catch (error) {
    console.warn('setMessageFavourite error: ', error);
    return null;
  }
}

//* Chat users
export async function fetchGetChatUsers(params: ChatUsersFiltersRequest) {
  try {
    // בניית מבנה הפילטרים החדש
    const filters: any[] = [];
    const conditions: any[] = [];

    // פילטר isOpen - משתמש ב-lastSession.status
    if (params.filter_by_open === 'open') {
      conditions.push({
        field: 'lastSession.status',
        operator: 'eq',
        value: 'open',
      });
    } else if (params.filter_by_open === 'close') {
      conditions.push({
        field: 'lastSession.status',
        operator: 'eq',
        value: 'closed',
      });
    }
    // אם 'all' - לא נוסיף פילטר

    // פילטר unreadCount
    if (params.isOnlyUnread) {
      conditions.push({
        field: 'unreadCount',
        operator: 'gt',
        value: 0,
      });
    }

    // פילטר label
    if (params.label && params.label !== '' && params.label !== 'noLabel') {
      conditions.push({
        field: 'label',
        operator: 'contains',
        value: params.label,
      });
    } else if (params.label === 'noLabel') {
      conditions.push({
        field: 'label',
        operator: 'isnull',
      });
    }

    // פילטר search_term (חיפוש ב-name או phone)
    if (params.search_term && params.search_term.trim() !== '') {
      filters.push({
        logic: 'or',
        conditions: [
          {
            field: 'name',
            operator: 'contains',
            value: params.search_term,
          },
          {
            field: 'phone',
            operator: 'contains',
            value: params.search_term,
          },
        ],
      });
    }

    // פילטר isBot
    if (params.is_bot) {
      conditions.push({
        field: 'activeBot',
        operator: 'isnotnull',
      });
    }

    // פילטר assigneeFilter - עכשיו נשתמש ב-operators לפי התצוגה
    // במקום לשלוח רשימה של UDID, נשתמש ב-operators כמו ne, eq, isnull, isnotnull
    // משתמש ב-managerId במקום assigneeId
    
    // אם assigneeFilter מכיל 'my' - זה אומר "שיחות שלי"
    if (params.assigneeFilter && params.assigneeFilter.length > 0) {
      if (params.assigneeFilter.includes('my')) {
        // "שיחות שלי" - נשלח managerId eq <currentUser.Udid>
        const currentUser = store.getState().user;
        if (currentUser?.Udid) {
          conditions.push({
            field: 'managerId',
            operator: 'eq',
            value: currentUser.Udid,
          });
        }
      } else if (params.assigneeFilter.includes('notMy')) {
        // "שיחות של אחרים" - נשלח managerId ne <currentUser.Udid> AND managerId isnotnull
        const currentUser = store.getState().user;
        if (currentUser?.Udid) {
          conditions.push({
            field: 'managerId',
            operator: 'ne',
            value: currentUser.Udid,
          });
          conditions.push({
            field: 'managerId',
            operator: 'isnotnull',
          });
        }
      } else if (params.assigneeFilter.includes('no-assign')) {
        // "לא משויכות" - נשלח managerId isnull
        conditions.push({
          field: 'managerId',
          operator: 'isnull',
        });
      } else {
        // אם זה רשימה של UDID (למקרים מיוחדים), נשתמש ב-in
        conditions.push({
          field: 'managerId',
          operator: 'in',
          value: params.assigneeFilter,
        });
      }
    }

    // פילטר: רק אנשי קשר שיש להם lastMessageObject
    conditions.push({
      field: 'lastMessageObject.id',
      operator: 'isnotnull',
    });

    // הוסף את כל ה-conditions ל-filters אם יש כאלה
    if (conditions.length > 0) {
      filters.push({
        conditions: conditions,
      });
    }

    // הוסף פילטרים מותאמים (custom filters) אם יש
    if (params.customFilters && params.customFilters.length > 0) {
      const customApiFilters = convertCustomFiltersToApiFilters(
        params.customFilters,
        params.customFiltersLogic,
      );
      filters.push(...customApiFilters);
    }

    // בניית ה-body המלא
    const requestBody: any = {
      filters: filters.length > 0 ? filters : undefined,
      limit: params.page_number || 50,
      offset: params.page_offset || 0,
      orderBy: params.order_by === 'last_message_date' ? 'lastMessageObject.dateCreated' : 'lastMessageObject.dateCreated',
      orderDirection: params.order_descending ? 'desc' : 'asc',
    };

    // הסר שדות undefined
    Object.keys(requestBody).forEach((key) => {
      if (requestBody[key] === undefined) {
        delete requestBody[key];
      }
    });

    // קריאה ל-API החדש עם הפילטרים
    const contacts = await fetcher('post', ['/api/services/getContacts', { data: requestBody }], 'servernode') as ContactResponse[] | null;
    
    if (contacts && Array.isArray(contacts)) {
      // מיפוי כל אנשי הקשר למבנה ChatUserModel
      const chatUsers = contacts.map(mapContactToChatUser);
      
      // החזרת המבנה הישן כדי לא לשבור את הקוד הקיים
      return {
        result: true,
        allChatUsers: {
          result: true,
          chatUsers: chatUsers,
        },
        myChatUsers: {
          result: true,
          chatUsers: chatUsers, // כרגע אותו דבר, בהמשך ניתן לסנן לפי assignee
        },
      };
    } else {
      return null;
    }
  } catch (error) {
    console.warn('fetchGetChatUsers error: ', error);
    return null;
  }
}

export const fetchGetBotStatus = async (phone: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/getChatUserBotStatus',
        {
          data: {
            chatUserPhoneId: phone,
          },
        },
      ],
      'history',
    );
    if (data) {
      return data as ChatUserBotStatusType;
    } else return null;
  } catch (error) {
    console.warn('getChatUserMeta error: ', error);
    return null;
  }
};

export const fetchStopUserBots = async (phone: string, botId: number) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/stopUserBots',
        {
          data: {
            userPhoneNumberId: phone,
            botId,
          },
        },
      ],
      'history',
    );
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('fetchStopUserBots error: ', error);
    return null;
  }
};

export const fetchGetChatUserConversations = async (phone: string) => {
  try {
    const data = await fetcher('get', `/api/services/getChatUserConversations/${phone}/1/10`, 'history');
    if (data) return data as { result: boolean; readyLogsList: Array<ChatUserConversation> };
    else return null;
  } catch (error) {
    console.warn('fetchGetChatUserConversations error: ', error);
    return null;
  }
};

export const fetchUpdateCovnersationLog = async (
  id: number,
  name: string | null,
  priority: number | null,
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/updateCovnersationLog',
        {
          data: {
            id,
            name,
            priority,
          },
        },
      ],
      'history',
    );
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('fetchUpdateCovnersationLog error: ', error);
    return null;
  }
};

export const fetchForwardMessagesToChatUsers = async (
  messagesList: number[],
  chatUsersPhoneList: string[],
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/forwardMessage',
        {
          data: {
            messagesList,
            chatUsersPhoneList,
          },
        },
      ],
      'messages',
    );
    if (data) return data as { result: boolean };
    else return null;
  } catch (error) {
    console.warn('fetchForwardMessagesToChatUsers error: ', error);
    return null;
  }
};
